import React from 'react';
import { Icons } from '../../constants';
import { UserProfile, AppTheme, Workspace } from '../../types';

import { SidebarContainer, SidebarItem, SidebarDivider, SidebarButton } from '../ui/SidebarPrimitives';

interface SidebarProps {
    activeTab: string;
    setActiveTab: (tab: string) => void;
    isCollapsed: boolean;
    setIsCollapsed: (collapsed: boolean) => void;
    onCreateBoard: () => void;
    userProfile?: UserProfile | null;
    currentTheme: AppTheme;
    onThemeChange: (theme: AppTheme) => void;
    onLogout?: () => void;
    isProfileMenuOpen: boolean;
    setIsProfileMenuOpen: (isOpen: boolean) => void;
    showThemeOptions: boolean;
    setShowThemeOptions: (show: boolean) => void;
    profileMenuRef: React.RefObject<HTMLDivElement>;
    // Workspace props
    workspaces: Workspace[];
    activeWorkspaceId: string | null;
    onSelectWorkspace: (id: string | null) => void;
    onCreateWorkspace: (name: string, color?: string) => void;
    onDeleteWorkspace: (id: string) => void;
    onRenameWorkspace: (id: string, name: string) => void;
    workspaceColors: string[];
}

export const Sidebar: React.FC<SidebarProps> = ({
    activeTab,
    setActiveTab,
    isCollapsed,
    setIsCollapsed,
    onCreateBoard,
    userProfile,
    currentTheme,
    onThemeChange,
    onLogout,
    isProfileMenuOpen,
    setIsProfileMenuOpen,
    showThemeOptions: _showThemeOptions,
    setShowThemeOptions: _setShowThemeOptions,
    profileMenuRef,
    // Workspace props
    workspaces,
    activeWorkspaceId,
    onSelectWorkspace,
    onCreateWorkspace,
    onDeleteWorkspace,
    onRenameWorkspace,
    workspaceColors
}) => {
    // Use static path from public folder
    const appLogoUrl = '/notescape.png';

    return (
        <SidebarContainer
            side="left"
            width="w-80"
            collapsed={isCollapsed}
            className="hidden lg:flex py-6 transition-all duration-500 ease-[cubic-bezier(0.25,0.1,0.25,1)] group/sidebar"
        >
            <button
                onClick={() => setIsCollapsed(!isCollapsed)}
                className="absolute -right-3 top-10 w-6 h-6 bg-[#0f0f10] border border-white/10 rounded-full flex items-center justify-center text-zinc-400 hover:text-white hover:border-indigo-500/50 shadow-sm z-50 opacity-0 group-hover/sidebar:opacity-100 transition-all duration-200 hover:scale-110 active:scale-95"
            >
                <Icons.ChevronRight className={`w-3.5 h-3.5 transition-transform duration-300 ${isCollapsed ? '' : 'rotate-180'}`} />
            </button>

            {/* Logo Section */}
            <div className={`flex items-center ${isCollapsed ? 'justify-center mb-6' : 'gap-4 mb-6 px-4'} transition-all duration-500`}>
                <div className={`flex items-center justify-center shrink-0 bg-white/5 rounded-2xl border border-white/5 p-2 ${isCollapsed ? 'w-10 h-10' : 'w-10 h-10'} transition-shadow duration-300`}>
                    <img src={appLogoUrl} alt="NoteSpace" className="w-full h-full object-contain opacity-90" />
                </div>
                <div className={`overflow-hidden transition-all duration-500 ${isCollapsed ? 'w-0 opacity-0' : 'w-auto opacity-100 flex-1'}`}>
                    <span className="font-bold text-lg tracking-tight text-white">NoteSpace</span>
                </div>
            </div>

            {/* New Board Button */}
            <div className="mb-6 px-2">
                <button
                    onClick={onCreateBoard}
                    className={`
                        group flex items-center justify-center relative overflow-hidden transition-all duration-300
                        ${isCollapsed
                            ? 'w-10 h-10 mx-auto rounded-xl bg-white/5 text-indigo-400 border border-white/5 hover:bg-white/10'
                            : 'w-full py-3.5 px-3 rounded-2xl bg-indigo-600/10 hover:bg-indigo-600/20 text-indigo-300 hover:text-indigo-200 border border-indigo-500/20 gap-2'
                        }
                    `}
                >
                    <Icons.Plus className={`${isCollapsed ? 'w-5 h-5' : 'w-4 h-4'}`} />
                    <span className={`text-[13px] font-bold ${isCollapsed ? 'hidden' : 'block'}`}>New Board</span>
                </button>
            </div>

            {/* Navigation */}
            <nav className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden scrollbar-none space-y-1 px-2">
                <div className="space-y-0.5">
                    <SidebarItem
                        icon={<Icons.Dashboard />}
                        label="Dashboard"
                        isActive={activeTab === 'dashboard'}
                        collapsed={isCollapsed}
                        onClick={() => setActiveTab('dashboard')}
                        color="text-sky-400"
                    />
                    <SidebarItem
                        icon={<Icons.Brain />}
                        label="Smart View"
                        isActive={activeTab === 'smartview'}
                        collapsed={isCollapsed}
                        onClick={() => setActiveTab('smartview')}
                        color="text-violet-400"
                    />
                    <SidebarItem
                        icon={<Icons.Recent />}
                        label="Recent"
                        isActive={activeTab === 'recent'}
                        collapsed={isCollapsed}
                        onClick={() => setActiveTab('recent')}
                        color="text-amber-400"
                    />
                    <SidebarItem
                        icon={<Icons.StarSidebar />}
                        label="Favorites"
                        isActive={activeTab === 'favorites'}
                        collapsed={isCollapsed}
                        onClick={() => setActiveTab('favorites')}
                        color="text-yellow-400"
                    />
                    <SidebarItem
                        icon={<Icons.TrashSidebar />}
                        label="Trash"
                        isActive={activeTab === 'trash'}
                        collapsed={isCollapsed}
                        onClick={() => setActiveTab('trash')}
                        color="text-rose-400"
                    />
                    <SidebarItem
                        icon={<Icons.Sparkles />}
                        label="Prompt Library"
                        isActive={activeTab === 'prompts'}
                        collapsed={isCollapsed}
                        onClick={() => setActiveTab('prompts')}
                        color="text-fuchsia-400"
                    />
                </div>


            </nav>

            {/* Profile Section */}
            <div className="mt-auto pt-4 border-t border-white/5 space-y-2 relative" ref={profileMenuRef}>
                <button
                    onClick={() => setIsProfileMenuOpen(!isProfileMenuOpen)}
                    className={`w-full flex items-center ${isCollapsed ? 'justify-center' : 'gap-3 px-4'} py-2 
                        hover:bg-white/5 cursor-pointer transition-all duration-300 group 
                        ${isProfileMenuOpen ? 'bg-white/5' : ''}`}
                >
                    <div className="w-9 h-9 rounded-full p-[2px] bg-indigo-500/20 shrink-0 shadow-sm border border-white/10 group-hover:border-indigo-500/50 transition-colors">
                        <img
                            src={userProfile?.avatar || "https://picsum.photos/seed/user/100/100"}
                            alt="user"
                            className="w-full h-full object-cover rounded-full"
                        />
                    </div>

                    <div className={`flex-1 min-w-0 text-left overflow-hidden transition-all duration-500 ${isCollapsed ? 'w-0 opacity-0 hidden' : 'w-auto opacity-100 block'}`}>
                        <p className="text-sm font-bold truncate text-zinc-200 group-hover:text-white transition-colors">
                            {userProfile?.name || 'Guest User'}
                        </p>
                        <p className="text-[10px] text-zinc-500 font-bold tracking-wider uppercase">Pro Account</p>
                    </div>

                    {!isCollapsed && <Icons.MoreHorizontal className="w-4 h-4 text-zinc-500 group-hover:text-zinc-300 transition-colors" />}
                </button>

                {isProfileMenuOpen && (
                    <div className={`
                        absolute bottom-full left-0 mb-2 
                        bg-[#0f0f10] border border-white/10
                        rounded-xl shadow-2xl overflow-hidden z-[60] 
                        animate-in slide-in-from-bottom-2 duration-200 
                        ${isCollapsed ? 'w-56 ml-2' : 'w-[calc(100%-1rem)] mx-2'}
                    `}>
                        <div className="p-1 space-y-0.5">
                            <p className="text-[10px] font-bold text-zinc-500 uppercase tracking-wider px-3 py-2">Account</p>
                            <SidebarButton
                                variant="ghost"
                                className="w-full justify-start px-3"
                                icon={Icons.User}
                                onClick={() => { setIsProfileMenuOpen(false); setActiveTab('settings'); }}
                            >
                                Profile
                            </SidebarButton>
                            <SidebarButton
                                variant="ghost"
                                className="w-full justify-start px-3"
                                icon={Icons.Settings}
                                onClick={() => { setIsProfileMenuOpen(false); setActiveTab('settings'); }}
                            >
                                Settings
                            </SidebarButton>



                            <SidebarButton
                                variant="ghost"
                                className="w-full justify-start px-3 text-indigo-400 hover:text-indigo-300 hover:bg-indigo-500/10"
                                icon={Icons.Download}
                                onClick={() => { setIsProfileMenuOpen(false); window.open('/download', '_blank'); }}
                            >
                                Get Desktop App
                            </SidebarButton>

                            <SidebarDivider className="my-1" />

                            <p className="text-[10px] font-bold text-zinc-500 uppercase tracking-wider px-3 py-2">Appearance</p>
                            <div className="flex items-center gap-1 px-2 pb-2">
                                {[
                                    { id: 'system', icon: Icons.Monitor, label: 'Auto' },
                                    { id: 'light', icon: Icons.Sun, label: 'Light' },
                                    { id: 'dark', icon: Icons.Moon, label: 'Dark' }
                                ].map((theme) => (
                                    <button
                                        key={theme.id}
                                        onClick={() => onThemeChange(theme.id as any)}
                                        className={`flex-1 p-2 rounded-lg flex flex-col items-center gap-1 transition-all border ${currentTheme === theme.id
                                            ? 'bg-white/10 border-white/10 text-white'
                                            : 'bg-transparent border-transparent hover:bg-white/5 text-zinc-500'
                                            }`}
                                        title={theme.label}
                                    >
                                        <theme.icon className="w-3.5 h-3.5" />
                                    </button>
                                ))}
                            </div>

                            <SidebarDivider className="my-1" />

                            <SidebarButton
                                variant="danger"
                                className="w-full justify-start px-3"
                                icon={Icons.LogOut}
                                onClick={() => { setIsProfileMenuOpen(false); onLogout?.(); }}
                            >
                                Log Out
                            </SidebarButton>
                        </div>
                    </div>
                )}
            </div>
        </SidebarContainer >
    );
};

